import { NewsOverviewItemData } from '@beat-venues/common-types';
import {
    Container,
    EventCollection,
    Heading,
    NewsCard,
    SocialList,
    Text,
} from '@beat-venues/ui/react';
import { useQuery } from '@tanstack/react-query';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import happyPhoto from '../../../public/assets/preferences/success/happy.gif';
import sadPhoto from '../../../public/assets/preferences/success/sad.gif';
import { useStoryStore } from '../../states/stroy-state';
import { createSocialItems } from '../../utils/socials.util';

interface SuccessPreferencesProps {
    isAllOptOut: boolean;
}

export const SuccessPreferences = ({
    isAllOptOut,
}: SuccessPreferencesProps): ReactElement => {
    const { t, i18n } = useTranslation();
    const { stories } = useStoryStore();

    const { data: newsArticle } = useQuery<NewsOverviewItemData[]>({
        queryKey: ['news', i18n.language],
        queryFn: () =>
            fetch('/api/contentful/news', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    locale: i18n.language,
                    tag: 'channel-my-beat',
                }),
            }).then((res) => res.json()),
    });

    return (
        <div className="mb-8 mt-20">
            <Heading className="text-left text-2xl font-bold">
                {t(
                    `${
                        isAllOptOut
                            ? 'preferences.success.headerSad'
                            : 'preferences.success.headerHappy'
                    }`
                )}
            </Heading>
            <div className="lg:ga-2 flex flex-col-reverse gap-4 lg:flex-row">
                <div>
                    <Text size="xl">
                        {t(
                            `${
                                isAllOptOut
                                    ? 'preferences.success.descriptionSad'
                                    : 'preferences.success.descriptionHappy'
                            }`
                        )}
                    </Text>
                </div>
                {isAllOptOut ? (
                    <img alt="sad" src={sadPhoto} />
                ) : (
                    <img alt="happy" src={happyPhoto} />
                )}
            </div>

            {!isAllOptOut && (
                <div>
                    {stories && (
                        <EventCollection
                            className="mt-32"
                            events={stories}
                            layout="default"
                            title={t('storyBanner.title') ?? undefined}
                        />
                    )}
                    {newsArticle && newsArticle.length > 0 && (
                        <Container maxWidth="xl">
                            <Heading as="h3" size="h3">
                                {t('preferences.success.readToo')}
                            </Heading>
                            <div className="is-highlighted group grid gap-x-4 gap-y-7 pb-7 text-left md:grid-cols-12 ">
                                {newsArticle.slice(0, 3)?.map((item, index) => {
                                    return (
                                        <NewsCard
                                            key={item.slug}
                                            className={`text-left ${
                                                index < 3
                                                    ? 'md:col-span-6 lg:col-span-4'
                                                    : 'md:col-span-6 lg:col-span-3'
                                            }`}
                                            date={item.date}
                                            image={item.image}
                                            isFirstThree={false}
                                            isLarge={false}
                                            slug={item.slug}
                                            title={item.title}
                                            venueId={item.venueId}
                                        />
                                    );
                                })}
                            </div>
                        </Container>
                    )}

                    <div className="mt-6">
                        <Heading as="h3" size="h3">
                            {t('preferences.success.socialsTitle')}
                        </Heading>
                        <SocialList
                            className="xs:justify-start"
                            socialLinks={createSocialItems()}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
